// Firebase client: app initialisation plus every small, self-contained operation (auth, presence, chat,
// canvas, history, solo scores, word bank). Rooms live in rooms.js; services.js re-exports both.
import { initializeApp } from 'firebase/app';
import {
  GoogleAuthProvider, getAuth, getRedirectResult, signInAnonymously, signInWithPopup, signOut,
} from 'firebase/auth';
import {
  addDoc, collection, doc, getDoc, getDocs, getFirestore, limit, onSnapshot, orderBy, query, serverTimestamp,
  setDoc, updateDoc, where,
} from 'firebase/firestore';
import {
  get, getDatabase, limitToLast, onDisconnect, onValue, orderByKey, push, ref, remove, set,
  query as rtQuery, serverTimestamp as rtServerTimestamp,
} from 'firebase/database';
import { getFunctions } from 'firebase/functions';
import { getStorage } from 'firebase/storage';
import { designTokens } from '../theme/theme';

// Project config comes from the REACT_APP_FIREBASE_* variables (Firebase Console → Project Settings → Your Apps).
const app = initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
});

export const db = getFirestore(app);
export const rtdb = getDatabase(app);
export const auth = getAuth(app);
export const functions = getFunctions(app, 'europe-west3');
export const storage = getStorage(app);
export default app;

/**
 * Safe wrapper around updateDoc — silently ignores "not-found" errors that
 * occur when a room was deleted (e.g. host left) while a game loop is still
 * running. All other errors are re-thrown as normal.
 */
export const safeUpdateDoc = async (docRef, data) => {
  try {
    await updateDoc(docRef, data);
  } catch (err) {
    if (err?.code === 'not-found' || err?.message?.includes('No document to update')) {
      return;
    }
    throw err;
  }
};

// ─── Avatar ────────────────────────────────────────────────────────────────

export const generateAvatar = (name) => {
  const colors = designTokens.avatarColors;
  return { color: colors[name.charCodeAt(0) % colors.length], initials: name.slice(0, 2).toUpperCase() };
};

// ─── Auth ──────────────────────────────────────────────────────────────────

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);
export const signInAnonymouslyUser = () => signInAnonymously(auth);
export const getGoogleRedirectResult = () => getRedirectResult(auth);
export const signOutUser = () => signOut(auth);
export const getCurrentUser = () => auth.currentUser;

/**
 * Check if a display name is taken by any user OTHER than `excludeUid`.
 */
export const checkNameAvailableForUid = (name, excludeUid) => new Promise((resolve) => {
  onValue(ref(rtdb, 'onlineUsers'), (snap) => {
    const taken = Object.values(snap.val() || {}).some(
      (user) => user.uid !== excludeUid && user.name?.toLowerCase() === name.toLowerCase(),
    );
    resolve(!taken);
  }, { onlyOnce: true });
});

// ─── Presence ──────────────────────────────────────────────────────────────

export const setPlayerOnlineStatus = async (roomId, userId, isOnline) => {
  const presenceRef = ref(rtdb, `presence/${roomId}/${userId}`);
  if (isOnline) {
    await set(presenceRef, { online: true, lastSeen: rtServerTimestamp() });
    onDisconnect(presenceRef).set({ online: false, lastSeen: rtServerTimestamp() });
  } else {
    await set(presenceRef, { online: false, lastSeen: rtServerTimestamp() });
  }
};

// Online users (for name uniqueness + presence)
export const setUserOnline = async (uid, name) => {
  const userRef = ref(rtdb, `onlineUsers/${uid}`);
  await set(userRef, { uid, name, lastSeen: rtServerTimestamp() });
  onDisconnect(userRef).remove();
};

export const removeUserOnline = async (uid) => {
  await remove(ref(rtdb, `onlineUsers/${uid}`));
};

/**
 * Update a player's name both in the online list (RTDB) and in a specific room (Firestore).
 */
export const updatePlayerNameInRoom = async (userId, roomId, newName) => {
  await setUserOnline(userId, newName);
  if (roomId) {
    await safeUpdateDoc(doc(db, 'rooms', roomId), { [`players.${userId}.name`]: newName });
  }
};

export const listenOnlineUsers = (callback) => onValue(ref(rtdb, 'onlineUsers'), (snap) => {
  callback(Object.values(snap.val() || {}));
});

// ─── Room chat (RTDB) ──────────────────────────────────────────────────────

export const sendChatMessage = async (roomId, userId, playerName, text, type = 'chat') => {
  await push(ref(rtdb, `chat/${roomId}`), {
    userId,
    playerName,
    text,
    type, // chat | correct | system | hint
    time: rtServerTimestamp(),
  });
};

export const sendSystemMessage = (roomId, text) => sendChatMessage(roomId, 'system', 'System', text, 'system');

export const listenChat = (roomId, callback) => {
  // Only the newest 100 messages are ever delivered, so don't download the rest.
  const chatQuery = rtQuery(ref(rtdb, `chat/${roomId}`), limitToLast(100));
  return onValue(chatQuery, (snap) => {
    const val = snap.val();
    callback(val ? Object.entries(val).map(([id, msg]) => ({ id, ...msg })).slice(-100) : []);
  });
};

export const clearChat = async (roomId) => {
  await remove(ref(rtdb, `chat/${roomId}`));
};

// ─── Global chat (Firestore) ───────────────────────────────────────────────

export const sendGlobalMessage = async (userId, playerName, text) => {
  if (!userId || !text?.trim()) return;
  // Anonymous users cannot post to Global Chat (Firestore rules also block it)
  if (auth.currentUser?.isAnonymous) return;
  await addDoc(collection(db, 'globalChat'), {
    userId,
    name: playerName,
    text: text.trim(),
    timestamp: serverTimestamp(),
  });
};

export const listenGlobalChat = (callback, onError) => {
  const globalQuery = query(collection(db, 'globalChat'), orderBy('timestamp', 'desc'), limit(80));
  return onSnapshot(globalQuery, (snap) => {
    callback(snap.docs.map((item) => ({ id: item.id, ...item.data() })).reverse());
  }, onError);
};

// ─── Drawing canvas (RTDB for low latency) ─────────────────────────────────

export const pushStroke = async (roomId, stroke) => {
  await push(ref(rtdb, `canvas/${roomId}/strokes`), stroke);
};

export const undoLastStroke = async (roomId) => {
  // Push keys are chronological, so only the newest key needs to be fetched.
  const snapshot = await get(rtQuery(ref(rtdb, `canvas/${roomId}/strokes`), orderByKey(), limitToLast(1)));
  let lastStrokeKey = null;
  snapshot.forEach((strokeSnapshot) => {
    lastStrokeKey = strokeSnapshot.key;
  });
  if (!lastStrokeKey) return false;
  await remove(ref(rtdb, `canvas/${roomId}/strokes/${lastStrokeKey}`));
  return true;
};

// Live stroke: the in-progress stroke from the current drawer (overwritten on each move)
export const setLiveStroke = (roomId, stroke) => {
  set(ref(rtdb, `canvas/${roomId}/live`), stroke); // fire-and-forget, no await needed for low-latency feel
};

export const clearLiveStroke = async (roomId) => {
  await set(ref(rtdb, `canvas/${roomId}/live`), null);
};

export const clearCanvas = async (roomId) => {
  await set(ref(rtdb, `canvas/${roomId}`), { cleared: Date.now() });
};

export const listenCanvas = (roomId, onStrokes, onClear, onLive) => {
  // Detach only these callbacks; off(ref) would also remove other listeners on the path.
  const unsubStrokes = onValue(ref(rtdb, `canvas/${roomId}/strokes`), (snap) => {
    const val = snap.val();
    onStrokes(val ? Object.values(val) : []);
  });
  const unsubCleared = onValue(ref(rtdb, `canvas/${roomId}/cleared`), (snap) => {
    if (snap.val()) onClear();
  });
  const unsubLive = onLive
    ? onValue(ref(rtdb, `canvas/${roomId}/live`), (snap) => onLive(snap.val() || null))
    : null;

  return () => {
    unsubStrokes();
    unsubCleared();
    if (unsubLive) unsubLive();
  };
};

// ─── Game history ──────────────────────────────────────────────────────────

/**
 * Save a completed game to the user's history.
 * gameData: { gameType, roomId, rank, totalPlayers, winner, opponents }
 */
export const saveGameHistory = async (uid, gameData) => {
  if (!uid) return;
  // Anonymous users don't get game history saved (Firestore rules also block it)
  if (auth.currentUser?.isAnonymous) return;
  if (!auth.currentUser || auth.currentUser.uid !== uid) {
    throw new Error('Could not save game history: the signed-in user changed.');
  }
  const opponents = gameData.opponents || (gameData.rankedPlayers || [])
    .filter((player) => !player.isMe)
    .map((player) => player.name)
    .filter(Boolean);
  await addDoc(collection(db, 'users', uid, 'gameHistory'), {
    ...gameData,
    opponents,
    playedAt: serverTimestamp(),
  });
};

/**
 * Fetch a user's completed games, newest first.
 */
export const getUserGameHistory = async (uid) => {
  if (!uid) return [];
  const snap = await getDocs(query(collection(db, 'users', uid, 'gameHistory'), orderBy('playedAt', 'desc')));
  return snap.docs.map((item) => ({ id: item.id, ...item.data() }));
};

// ─── Solo scores ───────────────────────────────────────────────────────────

const LEGACY_LS_PREFIX = 'solo_best_';

// Earlier versions cached personal bests in localStorage; Firestore is now the only source.
(function purgeLegacyLocalBests() {
  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(LEGACY_LS_PREFIX))
      .forEach((key) => localStorage.removeItem(key));
  } catch { /* storage unavailable */ }
}());

/**
 * Save a solo game score to Firestore if it beats the stored best.
 */
export const saveSoloScore = async (uid, playerName, gameId, score) => {
  if (!uid || !gameId || score == null) return;
  try {
    const docRef = doc(db, 'soloScores', `${uid}_${gameId}`);
    const snap = await getDoc(docRef);
    if (!snap.exists() || score > (snap.data().score || 0)) {
      await setDoc(docRef, {
        uid,
        name: playerName || 'Player',
        gameId,
        score,
        updatedAt: serverTimestamp(),
      });
    }
  } catch (e) {
    console.warn('saveSoloScore Firestore failed:', e);
  }
};

/**
 * Get the player's saved personal best for a game from Firestore (0 if none).
 */
export const getSoloBest = async (uid, gameId) => {
  if (!uid || !gameId) return 0;
  try {
    const snap = await getDoc(doc(db, 'soloScores', `${uid}_${gameId}`));
    return snap.exists() ? snap.data().score || 0 : 0;
  } catch (e) {
    console.warn('getSoloBest failed:', e);
    return 0;
  }
};

/**
 * Listen to the best scores for a game (up to limitCount players). Returns an unsubscribe fn.
 */
export const listenSoloLeaderboard = (gameId, callback, limitCount = 200) => {
  const leaderboard = query(
    collection(db, 'soloScores'),
    where('gameId', '==', gameId),
    orderBy('score', 'desc'),
    limit(limitCount),
  );
  return onSnapshot(leaderboard, (snap) => callback(snap.docs.map((item) => item.data())), (err) => {
    console.warn('listenSoloLeaderboard error:', err);
    callback([]);
  });
};

// ─── Word bank ─────────────────────────────────────────────────────────────
// The bundled fallback WORD_BANK is large, so it is only ever loaded via dynamic import() to keep it out
// of the main chunk.

let fallbackBank = null;
const loadFallbackBank = async () => {
  if (!fallbackBank) {
    fallbackBank = (await import('../data/wordBank')).WORD_BANK;
  }
  return fallbackBank;
};

const ensureFallbackBank = async () => {
  try {
    await loadFallbackBank();
  } catch (e) {
    console.warn('Fallback word bank failed to load:', e);
  }
};

export const uploadWordBank = async () => {
  const { WORD_BANK } = await import('../data/wordBank');
  try {
    await setDoc(doc(db, 'wordBank', 'en'), { words: WORD_BANK.en });
    console.log('Success! All words are now in Firestore.');
  } catch (e) {
    console.error('Error uploading:', e);
  }
};

// In-memory cache for word banks with TTL (5 minutes)
const WORD_BANK_CACHE_TTL = 5 * 60 * 1000;
const wordBankCache = { en: null, loadedAt: 0 };

/**
 * Populate the word cache (Firestore, or the bundled fallback). Await this
 * before calling getWordChoices(), which is synchronous.
 */
export const loadWordBank = async (lang = 'en') => {
  const now = Date.now();
  // Return cached words if still fresh
  if (wordBankCache[lang] && now - wordBankCache.loadedAt < WORD_BANK_CACHE_TTL) {
    if (!wordBankCache.en?.length) await ensureFallbackBank();
    return;
  }

  try {
    const snap = await getDoc(doc(db, 'wordBank', lang));
    if (snap.exists()) {
      wordBankCache[lang] = snap.data().words || [];
      wordBankCache.loadedAt = now;
    }
  } catch (e) {
    console.warn('Word bank load failed, using fallback:', e);
    try {
      const bank = await loadFallbackBank();
      wordBankCache[lang] = bank[lang] || bank.en;
      wordBankCache.loadedAt = now;
    } catch (importErr) {
      console.warn('Fallback word bank failed to load:', importErr);
    }
  }

  // getWordChoices falls back to the bundled list when Firestore gave us nothing
  if (!wordBankCache.en?.length) await ensureFallbackBank();
};

export const getWordChoices = (count = 3, usedWords = []) => {
  const pool = wordBankCache.en?.length ? wordBankCache.en : (fallbackBank?.en || []);
  const available = pool.filter((word) => !usedWords.includes(word));
  const source = available.length >= count ? available : pool;
  return [...source].sort(() => Math.random() - 0.5).slice(0, count);
};

// Hints
export const generateHint = (word) => word.split(' ')
  .map((part) => part.split('').map((ch, i) => (i === 0 ? ch : '_')).join(''))
  .join(' ');

export const revealHintCharacter = (word, hint) => {
  const hintArr = hint.split('');
  const hidden = hintArr.map((c, i) => (c === '_' ? i : null)).filter((i) => i !== null);
  if (!hidden.length) return hint;
  const revealIdx = hidden[Math.floor(Math.random() * hidden.length)];
  return hintArr.map((c, i) => (i === revealIdx ? word[i] : c)).join('');
};
